import {
    Button,
    Classes,
    FormGroup,
    HTMLSelect,
    Icon,
    Intent,
    NonIdealState,
    Slider,
    Tooltip,
} from "@blueprintjs/core";
import classNames from "classnames";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import ResizeDetector from "react-resize-detector";

import {
    Configuration,
    ErrorBoundary,
    getDefaultChatRoomState,
    getDefaultLocalUserState,
    getDefaultMediaDevicesState,
    getDefaultRoomState,
    roomNameToDisplayName,
} from "../common";
import { MediaDevicesManager, PrivateMessageManager, PublicMessageManager } from "./data";
import JitsiConferenceManager from "./data/JitsiConferenceManager";
import { JitsiManagerChildProps } from "./data/JitsiManager";
import {
    ChatRoom,
    DanceFloorBackground,
    DanceFloorOptions,
    FullScreenButton,
    MediaDevices,
    RemoteAudioTracks,
    RemoteVideoGrid,
    SelfView,
} from "./ui";

import styles from "./DanceFloor.module.css";
// TODO: oops wrong location
import { HostMessagesManager } from "./performance";

// by the time this component is rendered, we expect lib-jitsi-meet to be loaed,
// the connection to the server to be established, and the conference to be
// initialized (but not joined)
export interface DanceFloorProps extends JitsiManagerChildProps {
    bottomRightView?: React.ReactNode;
    showHostAudioOptions?: boolean;
    hideSelfView?: boolean;
    hideHostView?: boolean;
}

@observer
export default class DanceFloor extends React.Component<DanceFloorProps, never> {
    public readonly displayName = "DanceFloor";
    public static readonly defaultProps = {
        showHostAudioOptions: true,
        hideSelfView: false,
        hideHostView: false,
    };

    @observable public roomState = getDefaultRoomState({ isHost: this.props.isHost });
    @observable public localUserState = getDefaultLocalUserState(this.props.room.myUserId());
    @observable public mediaDevicesState = getDefaultMediaDevicesState();
    @observable public chatRoomState = getDefaultChatRoomState();

    private containerEl?: HTMLDivElement | null;
    private handleContainerRef = (el: HTMLDivElement | null) => (this.containerEl = el);

    public async componentDidMount() {
        if (Configuration.AUTO_JOIN_ROOM) {
            this.joinRoom();
        }
    }

    public componentDidUpdate(prevProps: DanceFloorProps) {
        if (prevProps.isReconnecting && !this.props.isReconnecting) {
            // re-connected successfully
            this.joinRoom();
        }
    }

    public async componentWillUnmount() {
        if (this.roomState.roomJoined === "joined") {
            await this.props.room?.leave();
        }
    }

    public render() {
        const { jitsi, room, roomName, isReconnecting, hideSelfView, hideHostView } = this.props;

        return (
            <div className={classNames(styles.container, Classes.DARK)} ref={this.handleContainerRef}>
                <JitsiConferenceManager {...this.props} localUser={this.localUserState} roomState={this.roomState} />
                {!isReconnecting && !hideSelfView && (
                    <MediaDevicesManager {...this.props} mediaDevicesState={this.mediaDevicesState} />
                )}
                <div className={styles.remoteViewContainer}>
                    {this.roomState.enableDanceFloorBackground && <DanceFloorBackground />}
                    <ResizeDetector handleHeight={true}>
                        {({ height }: { height: number }) => (
                            <div
                                className={styles.optionsFixedContainer}
                                style={{ height: 32, top: `${height - 30}px` }}
                            >
                                <FullScreenButton containerEl={this.containerEl} />
                                <DanceFloorOptions
                                    room={room}
                                    roomState={this.roomState}
                                    localUser={this.localUserState}
                                />
                            </div>
                        )}
                    </ResizeDetector>
                    <div className={styles.remoteViewContents}>
                        {this.roomState.roomJoined === "joined" ? (
                            <ErrorBoundary>
                                <RemoteAudioTracks
                                    roomState={this.roomState}
                                    mediaDevicesState={this.mediaDevicesState}
                                    localUserState={this.localUserState}
                                />
                                <RemoteVideoGrid
                                    jitsi={jitsi}
                                    room={room}
                                    roomState={this.roomState}
                                    chatRoomState={this.chatRoomState}
                                />
                                <PrivateMessageManager
                                    jitsi={jitsi}
                                    room={room}
                                    chats={this.chatRoomState.private}
                                    roomState={this.roomState}
                                />
                            </ErrorBoundary>
                        ) : (
                            <NonIdealState
                                title={roomNameToDisplayName(roomName)}
                                icon="globe-network"
                                description={
                                    <Button
                                        text="Join the party"
                                        large={true}
                                        intent={Intent.SUCCESS}
                                        loading={this.roomState.roomJoined === "joining"}
                                        onClick={this.joinRoom}
                                    />
                                }
                            />
                        )}
                    </div>
                </div>
                <div
                    className={classNames(styles.selfChatAndHostContainer, {
                        [styles.collapsed]: this.localUserState.isBottomPanelCollapsed,
                    })}
                >
                    {!hideSelfView && (
                        <SelfView
                            {...this.props}
                            mediaDevices={this.mediaDevicesState}
                            localUser={this.localUserState}
                            roomState={this.roomState}
                        />
                    )}
                    {this.renderChatView()}
                    {!hideHostView && this.renderHostView()}
                </div>
            </div>
        );
    }

    private renderChatView() {
        const { jitsi, room, hideSelfView } = this.props;
        const { roomJoined } = this.roomState;
        const chatDisabled = this.localUserState.displayName === undefined;

        return (
            <div className={styles.chatViewContainer}>
                {roomJoined === "joined" ? (
                    <ErrorBoundary>
                        <ChatRoom
                            jitsi={jitsi}
                            room={room}
                            uiState={this.chatRoomState.public}
                            inputProps={{
                                placeholder: chatDisabled
                                    ? "↖️ Choose a nickname first... above your self video"
                                    : "Say something...",
                                disabled: chatDisabled,
                            }}
                            localUser={this.localUserState}
                            isSelfViewHidden={hideSelfView}
                            mentionUserIds={this.roomState.videoLayout.map((l) => l.userId)}
                        />
                        <PublicMessageManager jitsi={jitsi} room={room} uiState={this.chatRoomState.public} />
                    </ErrorBoundary>
                ) : (
                    <NonIdealState title="Chat" icon="chat" description="Join the party to chat" />
                )}
            </div>
        );
    }

    private renderHostView() {
        const { bottomRightView, showHostAudioOptions } = this.props;
        const content = this.maybeRenderSpotlightedUser() ?? bottomRightView;
        return (
            <div className={styles.hostViewContainer}>
                <div className={styles.hostVideoContainer}>
                    {content ?? (
                        <Tooltip
                            content="Content highlighted by the host will appear here"
                            // @ts-ignore
                            fill={true}
                        >
                            <NonIdealState title="Host stream" icon="mobile-video" />
                        </Tooltip>
                    )}
                </div>
                {showHostAudioOptions && this.renderHostAudioOptions()}
                <HostMessagesManager jitsi={this.props.jitsi} room={this.props.room} roomState={this.roomState} />
            </div>
        );
    }

    private maybeRenderSpotlightedUser() {
        const { room } = this.props;
        const { spotlightUserId } = this.roomState;

        if (spotlightUserId === room.myUserId()) {
            return (
                <NonIdealState
                    className={styles.selfSpotlight}
                    title="You are now in the spotlight!"
                    description="💃 Bust a move 🕺"
                    icon="star"
                />
            );
        }

        const layout = this.roomState.videoLayout.find((l) => l.userId === spotlightUserId);

        if (layout !== undefined && this.roomState.tracks.has(layout.userId)) {
            const track = this.roomState.tracks.get(layout.userId)!.find((t) => t.getType() === "video");
            const participant = room.getParticipantById(layout.userId);
            if (track !== undefined && participant !== undefined) {
                // TODO
                // const displayName =
                //     this.roomState.displayNames.get(layout.userId) ?? participant.getDisplayName();

                return (
                    <video
                        className={styles.remoteVideo}
                        ref={(el: HTMLVideoElement) => {
                            if (el != null) {
                                el.srcObject = track.stream;
                            }
                        }}
                        autoPlay={true}
                        playsInline={true}
                        muted={true}
                    />
                );
            }
        }
    }

    private renderHostAudioOptions() {
        const { mediaDevices } = this.props.jitsi;
        const { audioOutputDeviceId, audioOutputs } = this.mediaDevicesState;
        const isAudioOutputDeviceChangeAvailable = mediaDevices.isDeviceChangeAvailable("audiooutput");
        return (
            <>
                <div className={styles.hostAudioLevel}>
                    <Slider
                        min={0}
                        max={1}
                        stepSize={0.05}
                        value={this.localUserState.audioOutputLevel}
                        labelRenderer={false}
                        intent={Intent.SUCCESS}
                        onChange={(level: number) => (this.localUserState.audioOutputLevel = level)}
                    />
                </div>
                <MediaDevices style={{ justifyContent: "flex-end" }}>
                    <FormGroup label={<Icon icon="volume-up" />} labelFor="audio-output-select" inline={true}>
                        <HTMLSelect
                            id="audio-output-select"
                            minimal={true}
                            disabled={!isAudioOutputDeviceChangeAvailable}
                            value={isAudioOutputDeviceChangeAvailable ? audioOutputDeviceId : "default"}
                            options={audioOutputs.map(({ deviceId, label }) => ({
                                label,
                                value: deviceId,
                            }))}
                            onChange={({ target }) => (this.mediaDevicesState.audioOutputDeviceId = target.value)}
                        />
                    </FormGroup>
                </MediaDevices>
            </>
        );
    }

    @action
    private joinRoom = () => {
        const { room, reconnect } = this.props;
        if (this.roomState.roomJoined === "left") {
            reconnect();
        } else {
            room.join();
        }
        this.roomState.roomJoined = "joining";
    };
}
